import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiDelete, apiFetch, apiPatch, apiPost, apiPut } from "@/lib/api";
import { milestoneKeys } from "@/lib/query";

type MilestonePhase = "pre_construction" | "construction" | "post_construction";

interface Milestone {
  id: string;
  projectId: string;
  name: string;
  title: string | null;
  description: string | null;
  phase: MilestonePhase;
  sortOrder: number | null;
  contractDate: string | null;
  forecastDate: string | null;
  actualDate: string | null;
  dueDate: string | null;
  status: string | null;
  completedAt: string | null;
  linkedTaskId: string | null;
  linkedTaskName: string | null;
  isTemplateDerived: boolean;
  templateName: string | null;
  templateItemId: string | null;
}

interface MilestoneHistoryEntry {
  id: string;
  milestoneId: string;
  fieldChanged: string | null;
  priorValue: string | null;
  newValue: string | null;
  action: string | null;
  changedByUserId: string | null;
  changedByName: string | null;
  createdAt: string | null;
}

interface MilestoneTemplateItem {
  id: string;
  name: string;
  phase: MilestonePhase;
  sortOrder: number;
  linkedTaskName: string | null;
  isSystemDefault: boolean;
  templateId: string | null;
}

export type { Milestone, MilestoneHistoryEntry, MilestonePhase, MilestoneTemplateItem };

// ── Project Milestones ──

export function useAllProjectMilestones() {
  return useQuery({
    queryKey: milestoneKeys.all,
    queryFn: () => apiFetch<Milestone[]>("/milestones"),
  });
}

export function useProjectMilestones(projectId: string) {
  return useQuery({
    queryKey: milestoneKeys.project(projectId),
    queryFn: () => apiFetch<Milestone[]>(`/projects/${projectId}/milestones`),
    enabled: !!projectId,
  });
}

export function useCreateMilestone(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<Milestone>) =>
      apiPost<{ data: Milestone }>(`/projects/${projectId}/milestones`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: milestoneKeys.project(projectId) });
      toast.success("Milestone created");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useUpdateMilestone(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ milestoneId, ...data }: { milestoneId: string } & Partial<Milestone>) =>
      apiPatch<{ data: Milestone }>(`/projects/${projectId}/milestones/${milestoneId}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: milestoneKeys.project(projectId) });
      toast.success("Milestone updated");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useCompleteMilestone(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ milestoneId, actualDate }: { milestoneId: string; actualDate?: string }) =>
      apiPost<{ data: Milestone }>(
        `/projects/${projectId}/milestones/${milestoneId}/complete`,
        actualDate ? { actualDate } : {},
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: milestoneKeys.project(projectId) });
      toast.success("Milestone completed");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useLinkedMilestoneForTask(taskId: string | null, enabled = true) {
  return useQuery({
    queryKey: ["tasks", taskId, "linked-milestone"] as const,
    queryFn: () => apiFetch<{ data: Milestone | null }>(`/tasks/${taskId}/linked-milestone`),
    enabled: enabled && !!taskId,
  });
}

export function useReopenMilestone(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (milestoneId: string) =>
      apiPost<{ data: Milestone }>(`/projects/${projectId}/milestones/${milestoneId}/reopen`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: milestoneKeys.project(projectId) });
      toast.success("Milestone reopened");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useDeleteMilestone(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (milestoneId: string) =>
      apiDelete(`/projects/${projectId}/milestones/${milestoneId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: milestoneKeys.project(projectId) });
      toast.success("Milestone deleted");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useMilestoneHistory(projectId: string, milestoneId: string) {
  return useQuery({
    queryKey: milestoneKeys.history(milestoneId),
    queryFn: () =>
      apiFetch<MilestoneHistoryEntry[]>(`/projects/${projectId}/milestones/${milestoneId}/history`),
    enabled: !!projectId && !!milestoneId,
  });
}

export function useInstantiateDefaultMilestones(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiPost<{ data: Milestone[] }>(`/projects/${projectId}/milestones/instantiate-defaults`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: milestoneKeys.project(projectId) });
      toast.success("Default milestones created");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

// ── Milestone Template Items (Admin) ──

export function useMilestoneTemplateItems() {
  return useQuery({
    queryKey: milestoneKeys.templates,
    queryFn: () => apiFetch<MilestoneTemplateItem[]>("/admin/milestone-template-items"),
  });
}

export function useCreateMilestoneTemplateItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<MilestoneTemplateItem>) =>
      apiPost<{ data: MilestoneTemplateItem }>("/admin/milestone-template-items", data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: milestoneKeys.templates });
      toast.success("Template item created");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useUpdateMilestoneTemplateItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string } & Partial<MilestoneTemplateItem>) =>
      apiPatch<{ data: MilestoneTemplateItem }>(`/admin/milestone-template-items/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: milestoneKeys.templates });
      toast.success("Template item updated");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useDeleteMilestoneTemplateItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/admin/milestone-template-items/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: milestoneKeys.templates });
      toast.success("Template item deleted");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useReorderMilestoneTemplateItems() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { orderedIds: string[] }) =>
      apiPut<{ data: MilestoneTemplateItem[] }>("/admin/milestone-template-items/reorder", data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: milestoneKeys.templates });
      toast.success("Template items reordered");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}
